/**
 * Medición de la geometría realmente pintada de un estímulo.
 * El `<img>` ocupa todo el viewport con `object-fit: contain`; el rectángulo visible se calcula a
 * partir del rectángulo medido del elemento y del aspecto natural (bandas grises excluidas).
 * Todo en px CSS: `devicePixelRatio` solo se guarda como metadato.
 */
import { boundsFromElementRect, type ImageBounds, type Rect } from "@gazelab/analysis";

export interface ViewportInfo {
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
}

/** Versión pura (testeable) a partir de un rectángulo tipo DOMRect. */
export function measureFromRect(
  rect: Pick<DOMRect, "left" | "top" | "width" | "height">,
  naturalWidth: number,
  naturalHeight: number,
  viewport: ViewportInfo,
): ImageBounds {
  const r: Rect = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  return boundsFromElementRect(r, naturalWidth, naturalHeight, viewport.innerWidth, viewport.innerHeight, viewport.devicePixelRatio);
}

/** Mide un `<img>` ya decodificado y pintado. */
export function measureImageBounds(img: HTMLImageElement, win: ViewportInfo = window): ImageBounds {
  if (!img.naturalWidth || !img.naturalHeight) throw new Error("La imagen aún no está decodificada");
  return measureFromRect(img.getBoundingClientRect(), img.naturalWidth, img.naturalHeight, win);
}

/** Espera a que el navegador haya pintado el estado actual (doble requestAnimationFrame). */
export function afterPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}
